import React, { useState, useEffect } from 'react';
import { Check, Star, Zap, Box, Layers, Eye, TrendingUp, Clock, AlertTriangle, X, ArrowRight, ShieldCheck, Smartphone, ShoppingBag, Globe, Database, Cpu, Repeat, MousePointerClick, Maximize2, Play } from 'lucide-react';
import { clsx } from 'clsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

// --- Shared Components ---
const SectionHeader = ({ title, subtitle, desc }: any) => (
  <div className="mb-12">
    <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-widest uppercase border border-gray-200 rounded-full text-gray-500">
      {subtitle}
    </span>
    <h2 className="text-3xl md:text-4xl font-serif font-medium mb-4">{title}</h2>
    {desc && <p className="text-gray-500 max-w-2xl leading-relaxed">{desc}</p>}
  </div>
);

const StatusBadge = ({ status }: { status: 'success' | 'warning' | 'error' | 'neutral' }) => {
  const styles = {
    success: "bg-green-100 text-green-700 border-green-200",
    warning: "bg-yellow-100 text-yellow-700 border-yellow-200",
    error: "bg-red-50 text-red-600 border-red-100",
    neutral: "bg-gray-100 text-gray-600 border-gray-200"
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${styles[status]}`}>
    {status === 'success' ? 'Native' : status === 'warning' ? 'Manual' : status === 'error' ? 'No' : 'N/A'}
  </span>;
};

// --- Figure 1 Components ---
const ComparisonRow = ({ label, ge, hyper, sam, isHeader = false }: any) => (
  <div className={clsx(
    "grid grid-cols-4 gap-4 py-6 border-b border-gray-100 items-center text-sm",
    isHeader ? "font-bold text-gray-900 uppercase tracking-wider text-xs" : "text-gray-600 hover:bg-gray-50/50 transition-colors"
  )}>
    <div className={clsx("font-medium", isHeader ? "" : "text-gray-900")}>{label}</div>
    <div className={clsx("text-center relative", !isHeader && "font-semibold text-black")}>
      {ge}
      {!isHeader && <div className="absolute inset-0 bg-yellow-100/20 -z-10 -mx-4 rounded-lg" />}
    </div>
    <div className="text-center">{hyper}</div>
    <div className="text-center text-gray-400">{sam}</div>
  </div>
);

const Stars = ({ count }: { count: number }) => (
  <div className="flex justify-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <div key={i} className={clsx(
        "w-2 h-2 rounded-full",
        i < count ? "bg-black" : "bg-gray-200"
      )} />
    ))}
  </div>
);

// --- Figure 5 Data ---
// Stacked bar chart data
// GE-ROOM: API (0.75) + Labor (4.5) = 5.25
// Other AI: Dev (10) + Optimization (10) = 20
// Manual: Modeling (20) + Conversion (10) + Dev (10) = 40
const costData = [
  { name: 'GE-ROOM', part1: 0.75, part2: 4.5, part3: 0, total: 5.25 },
  { name: 'Other AI Tools', part1: 10, part2: 10, part3: 0, total: 20 },
  { name: 'Manual Modeling', part1: 20, part2: 10, part3: 10, total: 40 },
];

export const BenchmarkSection = () => {
  const [isJourneyOpen, setIsJourneyOpen] = useState(false);
  const [journeyStep, setJourneyStep] = useState(0);

  const steps = [
    { img: '/assets/step1-clean.png', label: '1. Product Selection', desc: 'TIMER STARTS' },
    { img: '/assets/step2.jpg', label: '2. AR Placement', desc: 'VIEW IN ROOM' },
    { img: '/assets/step3-clean.png', label: '3. Interaction', desc: 'EXPLORE DETAILS' },
    { img: '/assets/step4-clean.png', label: '4. Decision', desc: 'TIMER ENDS / BUY' },
  ];

  // Auto-play effect for journey
  useEffect(() => {
    if (!isJourneyOpen || journeyStep >= 4) return;
    
    const timer = setTimeout(() => {
      setJourneyStep(prev => prev + 1);
    }, 3000); // 3 seconds per slide

    return () => clearTimeout(timer);
  }, [isJourneyOpen, journeyStep]);

  // Reset when opening
  const openJourney = () => {
    setJourneyStep(0);
    setIsJourneyOpen(true);
  };

  return (
    <section className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* --- Figure 1: Landscape --- */}
        <div className="mb-32">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 mb-4 text-xs font-medium tracking-widest uppercase border border-gray-200 rounded-full">
              Market Analysis
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-medium mb-6">The 3D Intelligence Landscape</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Comparing functional capabilities across creative, research, and industrial verticals.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-8 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4">
                <div className="font-bold text-gray-400 text-xs uppercase tracking-wider self-end">Dimension</div>
                <div className="text-center relative">
                  <div className="text-xs font-bold text-yellow-600 uppercase tracking-wider mb-1">Industrial</div>
                  <div className="font-bold text-lg">GE-ROOM</div>
                  <div className="text-[10px] text-gray-500">Optimized for Retail/AR</div>
                  <div className="absolute inset-0 bg-yellow-50/50 -z-10 -mx-4 -my-8 border-x border-yellow-100" />
                </div>
                <div className="text-center self-end">
                  <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">Commercial</div>
                  <div className="font-bold text-lg">Hyper3D</div>
                </div>
                <div className="text-center self-end">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Research</div>
                  <div className="font-bold text-lg text-gray-400">SAM 3D</div>
                </div>
              </div>
            </div>

            <div className="p-8 pt-2">
              <ComparisonRow 
                label="Tech Essence" 
                ge="Generation + Optimization" 
                hyper="Generation-first" 
                sam="Perception-first" 
              />
              <ComparisonRow 
                label="Geometric Strategy" 
                ge="Multi-view Constraints" 
                hyper="End-to-End Prediction" 
                sam="2-Stage (Coarse→Fine)" 
              />
              <ComparisonRow 
                label="Input Source" 
                ge="Image / Multi-view" 
                hyper="Image / Multi-view" 
                sam="Image + Seg Prompt" 
              />
              <ComparisonRow 
                label="AR Adaptability" 
                ge="Top Tier (Scene-aware)" 
                hyper="Good (Heavy Mesh)" 
                sam="Experimental" 
              />
              <ComparisonRow 
                label="Texture Logic" 
                ge="Automated PBR" 
                hyper="Auto PBR" 
                sam="Projection Mapping" 
              />
              <ComparisonRow 
                label="Success Rate" 
                ge={<Stars count={5} />} 
                hyper={<Stars count={4} />} 
                sam={<Stars count={3} />} 
              />
            </div>
          </div>
        </div>

        {/* --- Figure 3: AR Readiness --- */}
        <div className="mb-32">
          <SectionHeader 
            title="AR Readiness & Capability" 
            subtitle="Figure 3" 
            desc="Can users view the model on mobile like it's real? GE-ROOM ensures scale accuracy and physical grounding."
          />
          
          <div className="space-y-6 max-w-3xl">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="p-2 bg-white rounded-lg shadow-sm text-green-600"><Smartphone className="w-5 h-5" /></div>
              <div>
                <h4 className="font-bold text-sm mb-1">Native AR Viewer</h4>
                <p className="text-xs text-gray-500">Built-in iOS/Android adaptive viewer. No 3rd party apps needed.</p>
              </div>
              <div className="ml-auto"><StatusBadge status="success" /></div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="p-2 bg-white rounded-lg shadow-sm text-blue-600"><Box className="w-5 h-5" /></div>
              <div>
                <h4 className="font-bold text-sm mb-1">1:1 Physical Scale</h4>
                <p className="text-xs text-gray-500">Centimeter-level alignment. Eliminates "floating furniture" issues.</p>
              </div>
              <div className="ml-auto"><StatusBadge status="success" /></div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="p-2 bg-white rounded-lg shadow-sm text-purple-600"><Layers className="w-5 h-5" /></div>
              <div>
                <h4 className="font-bold text-sm mb-1">Quad-dominant Mesh</h4>
                <p className="text-xs text-gray-500">Optimized for mobile GPUs. No chaotic triangles.</p>
              </div>
              <div className="ml-auto"><StatusBadge status="success" /></div>
            </div>
          </div>
        </div>

        {/* --- Figure 4: The Growth Engine --- */}
        <div className="mb-32">
          <SectionHeader 
            title="The Growth Engine: Industrial Integration & Business ROI" 
            subtitle="Figure 4" 
            desc="Mapping technical superiority directly to business outcomes. From seamless integration to massive conversion uplift."
          />

          <div className="bg-black text-white rounded-3xl p-8 md:p-12 overflow-hidden relative">
            {/* Background Grid */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20" />

            {/* Flowchart Container */}
            <div className="relative z-10 grid lg:grid-cols-3 gap-8 items-center mb-16">
              
              {/* 1. Input: Integration */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400"><Globe className="w-5 h-5" /></div>
                  <div className="text-sm font-bold uppercase tracking-wider text-gray-400">Input</div>
                </div>
                <h3 className="text-xl font-bold mb-2">Universal Integration</h3>
                <p className="text-sm text-gray-400 mb-4">Platform Agnostic API compatible with any CMS.</p>
                <div className="flex flex-wrap gap-2">
                  {['Magento', 'Salesforce', 'Custom Web', 'Odoo'].map(tag => (
                    <span key={tag} className="px-2 py-1 bg-white/10 rounded text-[10px] font-mono">{tag}</span>
                  ))}
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden lg:flex justify-center text-white/20"><ArrowRight className="w-8 h-8" /></div>

              {/* 2. UX: Experience */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-3 opacity-10"><Smartphone className="w-24 h-24" /></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400"><Eye className="w-5 h-5" /></div>
                  <div className="text-sm font-bold uppercase tracking-wider text-gray-400">User Experience</div>
                </div>
                <h3 className="text-xl font-bold mb-2">Interactive AR</h3>
                <p className="text-sm text-gray-400 mb-4">1:1 Scale & Physical Lighting. "What You See Is What You Get".</p>
                <div className="flex items-center gap-2 text-xs text-green-400">
                  <Check className="w-3 h-3" /> Instant Loading
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden lg:flex justify-center text-white/20"><ArrowRight className="w-8 h-8" /></div>

              {/* 3. Output: ROI */}
              <div className="bg-gradient-to-br from-green-900/40 to-green-800/10 border border-green-500/30 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-500/20 rounded-lg text-green-400"><TrendingUp className="w-5 h-5" /></div>
                  <div className="text-sm font-bold uppercase tracking-wider text-green-400">Business Output</div>
                </div>
                <h3 className="text-xl font-bold mb-2">Revenue Growth</h3>
                <p className="text-sm text-gray-400 mb-4">Direct impact on conversion and retention metrics.</p>
                <div className="text-2xl font-bold text-white">+40-94% <span className="text-sm font-normal text-gray-400">Conversion</span></div>
              </div>
            </div>

            {/* --- Interactive User Journey CTA --- */}
            <div className="mb-16 border-t border-white/10 pt-12 relative z-10 text-center">
              <h4 className="text-xl font-bold mb-4">Interactive User Journey</h4>
              <p className="text-gray-400 text-sm max-w-2xl mx-auto mb-8">
                Experience the complete customer journey from product selection to purchase decision.
                See how we track dwell time and conversion impact in real-time.
              </p>
              <button 
                onClick={openJourney}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-bold hover:bg-gray-100 transition-colors"
              >
                <Play className="w-5 h-5 fill-current" />
                Start Interactive Journey
              </button>
            </div>

            {/* --- Sources & Takeaway --- */}
            <div className="mt-16 pt-12 border-t border-white/10 grid md:grid-cols-2 gap-12 relative z-10">
              {/* Shopify */}
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Source: 3D Commerce & Augmented Reality (Official Blog & Whitepaper, 2019–2020)
                </div>
                <h4 className="text-lg font-bold text-white mb-3">Shopify</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Shopify reports that products with augmented reality (AR) experiences achieve up to a <span className="text-white font-bold">94% increase in conversion rate</span> compared to standard product listings.
                  This uplift is driven by longer user dwell time and reduced purchase uncertainty, as customers can better understand product scale, appearance, and fit before buying.
                </p>
              </div>

              {/* PwC */}
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Source: AR/VR Consumer Survey, 2019
                </div>
                <h4 className="text-lg font-bold text-white mb-3">PwC</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  PwC’s consumer research shows that AR experiences <span className="text-white font-bold">increase purchase confidence by more than 40%</span>.
                  Consumers are significantly more likely to complete a purchase after visually confirming product dimensions and real-world context through AR.
                </p>
              </div>
            </div>

            {/* Key Takeaway */}
            <div className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10 text-center relative z-10">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Key Takeaway</h4>
              <p className="text-lg text-gray-300 font-medium">
                AR-driven real-time visualization increases user engagement, strengthens purchase confidence, and directly improves conversion performance.
              </p>
            </div>

          </div>
        </div>

        {/* --- Figure 5: Cost Benchmark --- */}
        <div className="mb-32">
          <SectionHeader 
            title="API Cost Benchmark (True ROI)" 
            subtitle="Figure 5" 
            desc="Hidden costs matter. Comparing total ownership cost including modeling, optimization, and development."
          />

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 h-[400px] bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis unit="€" axisLine={false} tickLine={false} domain={[0, 50]} />
                  <Tooltip 
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Legend />
                  <Bar dataKey="part1" stackId="a" name="Base Cost" fill="#FCD34D" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="part2" stackId="a" name="Dev/Opt Cost" fill="#F97316" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="part3" stackId="a" name="Hidden Cost" fill="#DC2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-col justify-center gap-6">
              <div className="p-6 bg-green-50 border border-green-100 rounded-xl">
                <div className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">GE-ROOM Advantage</div>
                <div className="text-3xl font-serif font-medium mb-1">€5.25</div>
                <div className="text-sm text-gray-600">Total Cost per SKU</div>
                <div className="mt-4 text-xs text-green-800 bg-green-100 px-2 py-1 rounded w-fit">Best ROI</div>
              </div>

              <div className="p-6 bg-red-50 border border-red-100 rounded-xl opacity-70">
                <div className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">Manual Modeling</div>
                <div className="text-3xl font-serif font-medium mb-1">€30 - €50</div>
                <div className="text-sm text-gray-600">High Labor Cost</div>
                <div className="mt-4 text-xs text-red-800 bg-red-100 px-2 py-1 rounded w-fit">Slow & Expensive</div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Fullscreen Journey Modal */}
      {isJourneyOpen && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center text-white animate-in fade-in duration-300">
          <button 
            onClick={() => setIsJourneyOpen(false)} 
            className="absolute top-8 right-8 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-50"
          >
            <X className="w-8 h-8" />
          </button>
          
          {journeyStep < 4 ? (
            <div className="relative w-full h-full flex items-center justify-center p-8">
              <div className="relative max-w-5xl max-h-[80vh] w-full h-full flex items-center justify-center">
                <img 
                  src={steps[journeyStep].img} 
                  alt={steps[journeyStep].label} 
                  className="max-w-full max-h-full object-contain animate-in zoom-in-95 duration-500"
                />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-black/80 backdrop-blur-md px-12 py-6 rounded-2xl text-center border border-white/10 shadow-2xl min-w-[300px]">
                  <div className="text-3xl font-bold mb-2">{steps[journeyStep].label}</div>
                  <div className="text-sm text-green-400 font-mono tracking-widest uppercase">{steps[journeyStep].desc}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-6xl w-full p-8 grid md:grid-cols-3 gap-8 animate-in slide-in-from-bottom-10 duration-700">
              <div className="bg-white/10 p-8 rounded-3xl border border-white/10 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4 text-gray-400">
                  <Clock className="w-6 h-6" />
                  <span className="text-sm font-bold uppercase">Dwell Time</span>
                </div>
                <div className="text-5xl font-bold text-white mb-2">+250%</div>
                <div className="text-lg text-gray-400">45s → 3.5min</div>
                <p className="text-sm text-gray-500 mt-6 leading-relaxed">
                  Users spend minutes "trying" furniture in their living room.
                </p>
              </div>

              <div className="bg-white/10 p-8 rounded-3xl border border-white/10 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4 text-gray-400">
                  <Repeat className="w-6 h-6" />
                  <span className="text-sm font-bold uppercase">User Stickiness</span>
                </div>
                <div className="text-5xl font-bold text-white mb-2">+35%</div>
                <div className="text-lg text-gray-400">Returning Visitors</div>
                <p className="text-sm text-gray-500 mt-6 leading-relaxed">
                  "Try-before-you-buy" builds confidence and loyalty.
                </p>
              </div>

              <div className="bg-white/10 p-8 rounded-3xl border border-white/10 backdrop-blur-sm border-green-500/30 bg-green-900/20">
                <div className="flex items-center gap-3 mb-4 text-green-400">
                  <MousePointerClick className="w-6 h-6" />
                  <span className="text-sm font-bold uppercase">Conversion Rate</span>
                </div>
                <div className="text-5xl font-bold text-green-400 mb-2">+94%</div>
                <div className="text-lg text-green-200/60">Sales Uplift</div>
                <p className="text-sm text-green-200/40 mt-6 leading-relaxed">
                  Eliminates "scale anxiety" (Will it fit?), doubling purchase probability.
                </p>
              </div>
            </div>
          )}
          
          {/* Progress Indicators */}
          <div className="absolute bottom-12 flex gap-3">
            {[0,1,2,3,4].map(i => (
              <div 
                key={i} 
                className={`h-1.5 rounded-full transition-all duration-500 ${i === journeyStep ? 'w-12 bg-white' : 'w-3 bg-white/20'}`} 
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
